import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  BondProceedApiLookupItem,
  BondProceedOption,
  BondProceedSubtype,
  BondProceedSubtypeApiItem,
  DeleteBondProceedSubtypeResult,
  UpsertBondProceedSubtypeRequest,
} from './bond-proceed-subtype.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class BondProceedSubtypeService {
  private readonly endpoint = apiUrl('bondproceedsubtype');
  private readonly proceedEndpoint = apiUrl('bondproceed');

  constructor(private readonly http: HttpClient) {}

  getBondProceedSubtypes(): Observable<BondProceedSubtype[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeBondProceedSubtype(item))),
    );
  }

  getBondProceeds(): Observable<BondProceedOption[]> {
    return this.http.get<unknown>(this.proceedEndpoint).pipe(
      map((response) => this.mapProceedRows(response, (item) => this.normalizeProceedOption(item))),
    );
  }

  createBondProceedSubtype(payload: UpsertBondProceedSubtypeRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateBondProceedSubtype(id: number, payload: UpsertBondProceedSubtypeRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteBondProceedSubtype(id: number): Observable<DeleteBondProceedSubtypeResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteBondProceedSubtypeResult => ({ kind: 'success' })),
      catchError(() => of<DeleteBondProceedSubtypeResult>({
        kind: 'error',
        message: 'Unable to delete bond proceed subtype at this time. Please try again.',
      })),
    );
  }

  private normalizeBondProceedSubtype(item: BondProceedSubtypeApiItem): BondProceedSubtype {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      description: this.toText(item.description),
      proceedId: this.toNullableNumber(item.proceedId),
      proceedName: this.toText(item.proceedName),
    };
  }

  private normalizeProceedOption(item: BondProceedApiLookupItem): BondProceedOption {
    return {
      id: this.toNumber(item.proceedId),
      name: this.toText(item.name),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: BondProceedSubtypeApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private mapProceedRows<T>(response: unknown, mapper: (item: BondProceedApiLookupItem) => T): T[] {
    return this.extractRows(response).map((item) => mapper(item as BondProceedApiLookupItem));
  }

  private extractRows(response: unknown): BondProceedSubtypeApiItem[] {
    if (Array.isArray(response)) {
      return response as BondProceedSubtypeApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as BondProceedSubtypeApiItem[];
      }

      if (Array.isArray(items)) {
        return items as BondProceedSubtypeApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private toNullableNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value)
      ? value
      : null;
  }
}
